import * as Sentry from '@sentry/react';
import { axiosCustomInstance, getToken, isValidJWT, refreshToken, waitForToken } from '../helpers/axios';
import { UseQueryResult } from 'react-query';
import { Options, QueryInfos, useMyQuery } from '../helpers';
import { typeId } from './types';
import { saveUser } from '../../../localStorageAPI';
import { getDefaultGroup } from '../../../utils/user';
import { redirectToAuth } from '../../../utils/redirect';
import { ApiType, BaseUser, IDEmployee, Language } from '../../apiTypes';
import { isWebView } from '../../../utils/webview';

// A renew that answers 200 without the cookie ever landing used to loop forever.
const MAX_RENEW_ATTEMPTS = 3;

// The proactive renewal loop lives in useTokenRefresh, which owns its timer: getMe is reachable
// from mount, from the foreground event and from the offline transition, and each call used to
// start its own never-cleared chain.
export const getMe = (
    setConnected?: (boolean) => void,
    setGroupId?: (groupId: string) => void,
    setPassengers?: (passengers: BaseUser[]) => void,
    changeLanguage?: (lang: Language) => any,
    renewAttempt = 0,
): void => {
    if (!isValidJWT()) {
        if (renewAttempt >= MAX_RENEW_ATTEMPTS) {
            console.log(`Token still invalid after ${renewAttempt} renews, giving up`);
            setConnected?.(false);
            redirectToAuth('Code - renew-no-cookie');
            return;
        }
        const expiredToken = getToken();
        console.log('Renewing token cause already expired old token');
        getRenewJWT()
            .then(async () => {
                // refreshToken() already forged the cookie and notified the native app when
                // in a webview, we only have to wait for it to be readable here.
                if (isWebView()) {
                    await waitForToken(expiredToken);
                }
                getMe(setConnected, setGroupId, setPassengers, changeLanguage, renewAttempt + 1);
            })
            .catch((error) => {
                console.log('error during JWT Renew', error, 'status', error.response?.status);
                setConnected?.(false);
                if (error.response?.status === 403) {
                    redirectToAuth('Code - bqPLMcat', error);
                }
            });
    } else {
        console.log('Getting Profil');
        getProfil()
            .then((response) => {
                const { data }: { data: IDEmployee } = response;
                if (!data.groups?.length) {
                    console.log('User connected has no Group, Redirecting to Auth');
                    redirectToAuth('No groups', data);
                } else if (!data.contracts?.length) {
                    console.log('User connected has no Contract, Redirecting to Auth');
                    redirectToAuth('No contracts', data);
                } else {
                    console.log(data);
                    saveUser(data);
                    changeLanguage?.(data.language);
                    const passenger: BaseUser = {
                        id: data.id,
                        firstname: data.firstname,
                        lastname: data.lastname,
                        phone_number: (data.phones?.find((p) => p.primary) || data.phones?.[0])?.phone,
                        email: (data.emails?.find((p) => p.primary) || data.emails?.[0])?.email,
                    };
                    setPassengers?.([passenger]);
                    Sentry.setUser({ email: passenger.email });
                    setGroupId?.(getDefaultGroup(data).uuid);
                    // useTokenRefresh picks the renewal loop up from here, keyed on isConnected.
                    setConnected?.(true);
                }
            })
            .catch((error) => {
                console.log('error', error, error.response?.status);
                setConnected?.(false);
                if (error.response?.status === 403) {
                    redirectToAuth('Code - 1xq8yqxe', error);
                }
            });
    }
};

export const getUserOptin = (user_uuid: string): Promise<any> => {
    return axiosCustomInstance.get(`/api/user/optin/${user_uuid}`, {});
};

export const getProfil = (): Promise<any> => {
    return axiosCustomInstance.get('/auth/me', {});
};

export const getRenewJWT = (): Promise<any> => {
    return refreshToken();
};

export const getUndress = (): Promise<any> => {
    return axiosCustomInstance.get('/auth/undress', {});
};

export const getLogout = (): Promise<any> => {
    return axiosCustomInstance
        .get('/auth/logout', {})
        .then(() => {
            redirectToAuth();
        })
        .catch((error) => {
            if (error.response?.status === 403) {
                redirectToAuth('Code - RwMCx1OX', error);
            } else {
                // TODO change to toast or something better ?
                // eslint-disable-next-line no-alert
                console.error(error);
            }
        });
};

// ==========================
// ======= Queries ==========
// ==========================

const userOptinQuery = (variables: { user_uuid: string }): QueryInfos => {
    return {
        name: `user-optin-${variables?.user_uuid}`,
        path: `/api/user/optin/${variables?.user_uuid}`,
        type: typeId.GET_USER_OPTIN,
        apiType: ApiType.ID,
        params: {},
    };
};

export function useGetUserOptin<T = any>(options: Options<{ user_uuid: string }>): UseQueryResult<T, Error> {
    return useMyQuery<T, { user_uuid: string }>(userOptinQuery, options);
}
